/*
 * Home · Collections carousel (sections/home-collections.liquid).
 *
 * Copies the Replo carousel: the active card is centred in the viewport with
 * its neighbours peeking in, it advances every data-autoplay seconds (paused
 * on hover and focus, off for prefers-reduced-motion), loops endlessly,
 * follows touch swipes, horizontal wheel/trackpad gestures and arrow keys.
 * Slides are rendered three times (clones, real, clones); the active index
 * always settles in the real (middle) set. data-speed 0 jumps without a
 * transition, as Replo's "No Animation" setting does. The optional
 * previous/next arrows and dots (not in Replo) drive the same go().
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HomeCollections {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-hm-viewport]');
      this.track = root.querySelector('[data-hm-track]');
      this.status = root.querySelector('[data-hm-status]');
      this.prev = root.querySelector('[data-hm-prev]');
      this.next = root.querySelector('[data-hm-next]');
      const section = root.closest('.hm-collections');
      this.dots = section ? Array.from(section.querySelectorAll('[data-hm-dot]')) : [];
      this.slides = Array.from(this.track.children);
      this.count = Number(root.dataset.count) || 0;
      this.autoplay = (Number(root.dataset.autoplay) || 0) * 1000;
      this.speed = Number(root.dataset.speed) || 0;
      this.index = this.count; // first real slide
      this.paused = new Set();
      this.timer = null;
      this.lastWheel = 0;
      if (this.count < 1 || this.slides.length < this.count * 3) return;

      this.onResize = () => this.position(false);
      this.onTransitionEnd = (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.settle();
      };

      this.track.classList.add('is-ready');
      this.position(false);
      this.bind();
      this.schedule();
    }

    bind() {
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      // Pause on hover and while focus is inside, like Replo's pauseOnHover.
      this.root.addEventListener('mouseenter', () => this.pause('hover'));
      this.root.addEventListener('mouseleave', () => this.resume('hover'));
      this.root.addEventListener('focusin', () => this.pause('focus'));
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.resume('focus');
      });

      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToNearest(Number(dot.dataset.hmDot)));
      });

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(-1);
        }
      });

      // Horizontal wheel / trackpad: one card per gesture.
      this.viewport.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 10) return;
          event.preventDefault();
          const now = Date.now();
          if (now - this.lastWheel < 600) return;
          this.lastWheel = now;
          this.go(event.deltaX > 0 ? 1 : -1);
        },
        { passive: false }
      );

      // Touch / pen swipes. Mouse drags are left alone (Replo has dragging off).
      let start = null;
      this.viewport.addEventListener('pointerdown', (event) => {
        this.swiped = false;
        if (event.pointerType === 'mouse') return;
        start = { x: event.clientX, y: event.clientY };
      });
      this.viewport.addEventListener('pointerup', (event) => {
        if (!start) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) this.go(dx < 0 ? 1 : -1);
      });
      this.viewport.addEventListener('pointercancel', () => {
        start = null;
      });
      // A swipe that started on a card shouldn't follow its link.
      this.viewport.addEventListener('click', (event) => {
        if (this.swiped) {
          event.preventDefault();
          this.swiped = false;
        }
      });
      this.viewport.addEventListener('pointermove', (event) => {
        if (start && Math.abs(event.clientX - start.x) > 10) this.swiped = true;
      });
    }

    offsetFor(index) {
      const slide = this.slides[index];
      if (!slide) return 0;
      return this.viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
    }

    position(animate) {
      const useTransition = animate && this.speed > 0 && !reduceMotion.matches;
      this.track.style.transition = useTransition ? `transform ${this.speed}ms ease` : 'none';
      this.track.style.transform = `translate3d(${this.offsetFor(this.index)}px, 0, 0)`;
      if (!useTransition) this.settle();
    }

    // Move back into the real (middle) set without a visible jump.
    settle() {
      let next = this.index;
      while (next >= this.count * 2) next -= this.count;
      while (next < this.count) next += this.count;
      if (next !== this.index) {
        this.index = next;
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(${this.offsetFor(this.index)}px, 0, 0)`;
      }
    }

    go(delta) {
      this.settle();
      this.index += delta;
      this.position(true);
      this.update();
      this.schedule();
    }

    goTo(realIndex) {
      this.index = this.count + realIndex;
      this.position(false);
      this.update();
    }

    // Dots: move the shortest way round the loop to the chosen card.
    goToNearest(realIndex) {
      this.settle();
      const current = this.index - this.count;
      let delta = realIndex - current;
      if (delta > this.count / 2) delta -= this.count;
      if (delta < -this.count / 2) delta += this.count;
      if (delta) this.go(delta);
    }

    update() {
      const real = ((this.index % this.count) + this.count) % this.count;
      if (this.status) this.status.textContent = `Slide ${real + 1} of ${this.count}`;
      this.dots.forEach((dot, i) => {
        if (i === real) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.count < 2 || this.paused.size || reduceMotion.matches) return;
      this.timer = setTimeout(() => this.go(1), this.autoplay);
    }

    pause(reason) {
      this.paused.add(reason);
      clearTimeout(this.timer);
    }

    resume(reason) {
      this.paused.delete(reason);
      this.schedule();
    }

    destroy() {
      clearTimeout(this.timer);
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.onResize);
    }
  }

  const instances = new WeakMap();

  function init(scope) {
    scope.querySelectorAll('[data-hm-collections]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new HomeCollections(root));
    });
  }

  function destroy(scope) {
    scope.querySelectorAll('[data-hm-collections]').forEach((root) => {
      const instance = instances.get(root);
      if (instance && instance.destroy) instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-hm-slide]');
    const root = event.target.closest('[data-hm-collections]');
    const instance = root && instances.get(root);
    if (!slide || !instance || !instance.goTo) return;
    instance.pause('editor');
    instance.goTo(Number(slide.dataset.hmSlide));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-hm-collections]');
    const instance = root && instances.get(root);
    if (instance && instance.resume) instance.resume('editor');
  });
})();
